// AgentVerse/client/components/MessagingClient.js
import { ComponentBase } from "../core/ComponentBase.js";
import { ConversationList } from '/helpers/MessagingClient/ConversationList.js';
import { ChatView } from '/helpers/MessagingClient/ChatView.js';
import { NewChatModal } from '/helpers/MessagingClient/NewChatModal.js';
import { GroupSettingsModal } from '/helpers/MessagingClient/GroupSettingsModal.js';
import { ContactInfoModal } from '../helpers/MessagingClient/ContactInfoModal.js';

export class MessagingClient extends ComponentBase {
    static styles = `
        .messaging-client-container {
            display: flex;
            height: 100%; /* Full viewport height */
            overflow: hidden; /* Prevent outer scroll */
            background-color: var(--bs-body-bg);
        }

        .conversation-list-pane {
            width: 30%; /* Desktop: 30% for conversation list */
            min-width: 380px; /* Min width for conversation list */
            max-width: 380px; /* Max width, adjusted slightly */
            border-right: 1px solid var(--bs-border-color);
            display: flex; /* Ensure it's a flex container itself */
            flex-direction: column; /* Stack header, search, list vertically */
            background-color: var(--bs-tertiary-bg); /* Slightly different background for list pane */
        }

        .chat-view-pane {
            flex-grow: 1; /* Takes remaining space */
            display: flex;
            flex-direction: column; /* Header, MessageArea, InputArea vertically */
        }

        /* Mobile specific styles */
        @media (max-width: 768px) {
            .messaging-client-container.viewing-chat .conversation-list-pane {
                display: none !important; /* Hide conversation list when a chat is active */
            }
            .messaging-client-container:not(.viewing-chat) .chat-view-pane {
                display: none !important; /* Hide chat view when conversation list is active */
            }
            .conversation-list-pane, .chat-view-pane {
                width: 100% !important; /* Full width on mobile */
                max-width: 100% !important;
                border-right: none !important;
            }
        }

        .chat-view-placeholder {
            flex-grow: 1;
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            color: var(--bs-secondary-color);
            flex-direction: column;
            padding: 2rem; /* Add some padding */
        }
        .chat-view-placeholder i {
            font-size: 4rem;
            margin-bottom: 1rem;
        }


    `;

    constructor(config) {
        super(config);
        this.conversationList = null;
        this.chatView = null;
        this.newChatModal = null;

        this.activeConversationId = null; // This is the channelId
        this.myContactId = null; // Current user's MessagingContact ID

        // DOM Elements
        this.container = null;
        this.convListPaneElement = null;
        this.chatViewPaneElement = null;
        this.placeholderElement = null;
        this.optimisticMessages = new Map();


        // Bind resize handler once
        this._boundAdjustLayout = this.adjustLayoutForScreenSize.bind(this);
        this._boundHandleProjectSelected = this.handleProjectSelected.bind(this); // NEW
        this.subscribedChannelRooms = new Set();

        this.applyStyles();

    }

    async initialize() {
        this.container = document.createElement('div');
        this.container.className = 'messaging-client-container';
        this.container.id = `${this.config.instanceId}-main-wrapper`;

        this.conversationList = new ConversationList({
            appClient: this.appClient,
            instanceId: `${this.config.instanceId}-conv-list`,
            // Pass currentProjectId if available at this point, or update it later
            currentProjectId: this.currentProjectId
        });
        await this.conversationList.initialize();
        this.conversationList.on('conversationSelected', this.handleConversationSelected.bind(this));
        this.conversationList.on('newChatClicked', this.handleNewChatOrGroup.bind(this));

        this.chatView = new ChatView({
            appClient: this.appClient,
            instanceId: `${this.config.instanceId}-chat-view`,
            // Pass currentProjectId if available
            currentProjectId: this.currentProjectId
        });
        await this.chatView.initialize();
        this.chatView.on('backClicked', this.handleChatViewBack.bind(this));
        this.chatView.on('messageSent', this.handleMessageSentByChatView.bind(this));
        this.chatView.on('messagesViewed', this.handleMessagesViewed.bind(this));
        this.chatView.header.on('contactInfoClicked', this.handleShowContactInfo.bind(this));
        this.chatView.header.on('groupSettingsClicked', this.handleShowGroupSettings.bind(this));
        this.chatView.messageInput.on('typingStatusChanged', this.handleTypingStatusChangeFromInput.bind(this));

        this.newChatModal = new NewChatModal({
            appClient: this.appClient,
            instanceId: `${this.config.instanceId}-newchat-modal`,
            // Pass currentProjectId if available
            currentProjectId: this.currentProjectId
        });
        await this.newChatModal.initialize();
        this.newChatModal.on('dmContactSelected', this.handleDmContactSelectedFromModal.bind(this));
        this.newChatModal.on('groupCreationRequested', this.handleGroupCreationFromModal.bind(this));


        this.contactInfoModal = new ContactInfoModal({
            appClient: this.appClient,
            instanceId: `${this.config.instanceId}-contactinfo-modal`,
        });
        await this.contactInfoModal.initialize();
        this.contactInfoModal.on('closed', () => { /* Optional: handle modal close */ });
        this.contactInfoModal.on('startChatWithContact', this.handleDmContactSelectedFromModal.bind(this));

        this.groupSettingsModal = new GroupSettingsModal({
            appClient: this.appClient,
            instanceId: `${this.config.instanceId}-groupsettings-modal`,
            // channelId, myContactId, currentProjectId will be set when showing
        });
        await this.groupSettingsModal.initialize();
        this.groupSettingsModal.on('closed', () => { /* Optional: handle modal close if needed */ });
        this.groupSettingsModal.on('leftGroup', this.handleLeftGroup.bind(this)); // Listen for leave event
        this.groupSettingsModal.on('contactInfoClicked', this.handleShowContactInfo.bind(this));

        // Server events
        this.appClient.events.server.on('newMessage', this.handleNewMessageFromServer.bind(this));
        this.appClient.events.server.on('conversationUpdated', this.handleConversationUpdatedFromServer.bind(this));
        this.appClient.events.server.on('messagesRead', this.handleMessagesReadFromServer.bind(this)); // NEW

        this.appClient.events.server.on('newChannel', this.handleNewChannelFromServer.bind(this));
        this.appClient.events.server.on('channelDeleted', this.handleChannelDeletedFromServer.bind(this)); // --- ADD THIS ---
        this.appClient.events.server.on('channelMemberAdded', this.handleChannelMembershipChange.bind(this));
        this.appClient.events.server.on('channelMemberRemoved', this.handleChannelMembershipChange.bind(this));
        this.appClient.events.server.on('channelMemberRoleChanged', this.handleChannelMembershipChange.bind(this));
        this.appClient.events.server.on('youWereRemovedFromChannel', this.handleYouWereRemoved.bind(this));
        this.appClient.events.server.on('user_typing_status', this.handleUserTypingStatusFromServer.bind(this));

        window.addEventListener('resize', this._boundAdjustLayout);
        this.appClient.events.local.on('projectSelected', this._boundHandleProjectSelected);
        this.log.info("MessagingClient initialized with helpers.");
        await super.initialize();

    }

    async handleProjectSelected({ projectData }) {
        const newProjectId = projectData ? projectData._id : null;
        if (this.currentProjectId === newProjectId) {
            return;
        }
        this.currentProjectId = newProjectId;
        this.log.info(`Project selected in MessagingClient: ${this.currentProjectId}`);

        await this.unsubscribeFromAllTrackedChannelRooms();

        this.activeConversationId = null;
        this.myContactId = null;
        this.appClient.setSharedData('myMessagingContactId', null);

        // Update helpers with the new project ID
        this.conversationList.currentProjectId = this.currentProjectId;
        this.chatView.currentProjectId = this.currentProjectId;
        this.newChatModal.currentProjectId = this.currentProjectId;

        this.conversationList.setMyContactId(null);
        this.chatView.setMyContactId(null);
        this.conversationList.conversations = [];
        this.conversationList.filteredConversations = [];
        this.newChatModal.myContactId = null;

        if (this.currentProjectId) {
            await this.setupOnProjectChange();
        } else {
            await this.renderStructure();
            this.updateChatViewPaneContent();
            await this.conversationList.renderConversationItems();
            this.adjustLayoutForScreenSize();
            this.renderPlaceholder("Please select a project to start messaging.");
        }
    }


    // initAfterLogin is called by ComponentBase after login.
    // We'll use it to trigger the initial setup if a project is already selected.
    async initAfterLogin() {
        // If a project was selected *before* login (e.g., from localStorage by ProjectSelector)
        // currentProjectId would be set.
        const selectedProject = this.appClient.getSharedData('selectedProject');
        if (selectedProject) {
            await this.handleProjectSelected({ projectData: selectedProject });
        } else {
            // No project selected yet, show placeholder or initial state
            await this.renderStructure();
            this.updateChatViewPaneContent();
            this.adjustLayoutForScreenSize();
            this.renderPlaceholder("Please select a project to start messaging.");
        }
    }


    async setupOnProjectChange() {
        const currentAppUserId = this.appClient.getSharedData('userId');

        if (!currentAppUserId || !this.currentProjectId) {
            const errorMsg = `MessagingClient: Missing userId or currentProjectId. Current Project: ${this.currentProjectId}`;
            this.log.error(errorMsg);
            this.renderPlaceholder(this.currentProjectId ? "Error initializing chat for project." : "Please select a project to start messaging.");
            return;
        }

        try {
            const contact = await this.appClient.rpcClient.call('MessagingContactsRpc.findByAppUserId',
                { appUserId: currentAppUserId },
                { projectId: this.currentProjectId }
            );

            if (!contact) {
                const errorMsg = `MessagingContact for current user (appUserId: ${currentAppUserId}) not found in project '${this.currentProjectId}'. An admin needs to create this contact.`;
                this.log.error(errorMsg);
                this.renderPlaceholder(errorMsg);
                return;
            }
            this.myContactId = contact.contactId;
            this.appClient.setSharedData('myMessagingContactId', this.myContactId);

            this.conversationList.setMyContactId(this.myContactId);
            this.chatView.setMyContactId(this.myContactId);
            this.newChatModal.myContactId = this.myContactId;

            await this.conversationList.loadConversations();
            await this.subscribeToLoadedConversationRooms(); // NEW: Subscribe after loading
            await this.renderStructure(); // Await renderStructure
            this.updateChatViewPaneContent();
            this.adjustLayoutForScreenSize();

        } catch (error) {
            this.log.error(`Error during MessagingClient setupOnProjectChange for project ${this.currentProjectId}:`, error);
            this.renderPlaceholder(`Error initializing chat for project ${this.currentProjectId}: ${error.message}`);
        }
    }


    async renderStructure() { // Made async
        this.container.innerHTML = '';

        this.convListPaneElement = document.createElement('div');
        this.convListPaneElement.className = 'conversation-list-pane';
        // AWAIT the render method of conversationList
        this.convListPaneElement.appendChild(await this.conversationList.render());
        this.container.appendChild(this.convListPaneElement);

        this.chatViewPaneElement = document.createElement('div');
        this.chatViewPaneElement.className = 'chat-view-pane';
        this.container.appendChild(this.chatViewPaneElement);

        this.placeholderElement = document.createElement('div');
        this.placeholderElement.className = 'chat-view-placeholder';
        this.placeholderElement.innerHTML = `<i class="bi bi-chat-left-dots-fill"></i>
                                             <p data-i18n="Select a chat to start messaging">${this.t('Select a chat to start messaging')}</p>`;
        this.appClient.tx.applyTranslations(this.placeholderElement);
    }

    renderPlaceholder(message = "An error occurred, or no project/user context is available.") {
        if (this.container) {
            this.container.innerHTML = `<div class="chat-view-placeholder flex-grow-1 justify-content-center align-items-center d-flex">
                                           <i class="bi bi-exclamation-triangle-fill text-danger display-3 mb-3"></i>
                                           <p class="lead">${this.t(message)}</p>
                                       </div>`;
            this.appClient.tx.applyTranslations(this.container);
        }
    }

    async updateChatViewPaneContent() { // Made async
        if (!this.chatViewPaneElement) return;
        this.chatViewPaneElement.innerHTML = '';

        if (this.activeConversationId) {
            this.chatView.setActiveConversation(this.activeConversationId);
            // AWAIT the render method of chatView
            this.chatViewPaneElement.appendChild(await this.chatView.render());
        } else {
            this.chatViewPaneElement.appendChild(this.placeholderElement);
        }
    }


    async handleConversationSelected({ conversationId }) {
        this.log.info(`MessagingClient: Conversation selected: ${conversationId}`);
        if (this.activeConversationId === conversationId && window.innerWidth > 768) {
            return;
        }
        this.activeConversationId = conversationId;
        if (this.currentProjectId && conversationId && !this.subscribedChannelRooms.has(conversationId)) {
            try {
                await this.appClient.rpcClient.call('MessagingRpc.subscribeToChannelUpdates', { channelId: conversationId }, { projectId: this.currentProjectId });
                this.subscribedChannelRooms.add(conversationId);
            } catch (error) {
                this.log.error(`Failed to subscribe to selected channel ${conversationId} updates:`, error);
            }
        }
        await this.updateChatViewPaneContent();
        this.adjustLayoutForScreenSize();
        this.conversationList.resetUnreadCount(conversationId);
    }

    async handleChatViewBack() { // Made async
        this.activeConversationId = null;
        await this.updateChatViewPaneContent();
        this.adjustLayoutForScreenSize();
    }


    handleNewChatOrGroup() {
        if (!this.currentProjectId) {
            this.appClient.notifier.warn("Please select a project before starting a new chat or group.");
            return;
        }
        this.newChatModal.show();
    }

    async handleNewChannelFromServer(newChannelDataWithDetails) {
        if (newChannelDataWithDetails.projectId !== this.currentProjectId) {
            this.log.debug(`Ignoring newChannel event for project ${newChannelDataWithDetails.projectId}`);
            return;
        }
        this.log.info("MessagingClient: New channel event from server:", newChannelDataWithDetails);
        if (this.conversationList) {
            await this.conversationList.addNewConversation(newChannelDataWithDetails);
            // --- SUBSCRIBE TO NEW CHANNEL ROOM ---
            if (this.currentProjectId && newChannelDataWithDetails.channelId && !this.subscribedChannelRooms.has(newChannelDataWithDetails.channelId)) {
                try {
                    await this.appClient.rpcClient.call('MessagingRpc.subscribeToChannelUpdates', { channelId: newChannelDataWithDetails.channelId }, { projectId: this.currentProjectId });
                    this.subscribedChannelRooms.add(newChannelDataWithDetails.channelId);
                } catch (error) {
                    this.log.error(`Failed to subscribe to new channel ${newChannelDataWithDetails.channelId} updates:`, error);
                }
            }
            // --- END SUBSCRIBE ---
        }
    }




    async handleLeftGroup({ channelId }) {
        this.log.info(`User left group ${channelId}. Updating UI.`);
        if (this.activeConversationId === channelId) {
            this.activeConversationId = null;
            await this.updateChatViewPaneContent(); // Show placeholder
            this.adjustLayoutForScreenSize();
        }
        this.conversationList.removeConversation(channelId);
    }

    async handleYouWereRemoved({ channelId, projectId }) {
        if (projectId !== this.currentProjectId) {
            this.log.debug(`Ignoring youWereRemovedFromChannel event for project ${projectId}`);
            return;
        }
        this.log.info(`MessagingClient: Current user was removed from channel ${channelId}.`);

        if (this.conversationList) {
            this.conversationList.removeConversation(channelId);
        }

        if (this.activeConversationId === channelId) {
            this.activeConversationId = null;
            await this.updateChatViewPaneContent(); // Show placeholder
            this.adjustLayoutForScreenSize();
            this.appClient.notifier.info(this.t("You have been removed from the group."));
        }

        if (this.subscribedChannelRooms.has(channelId)) {
            try {
                await this.appClient.rpcClient.call('MessagingRpc.unsubscribeFromChannelUpdates', { channelId }, { projectId: this.currentProjectId });
                this.subscribedChannelRooms.delete(channelId);
            } catch (error) {
                this.log.error(`Failed to unsubscribe from channel ${channelId} after being removed:`, error);
            }
        }

    }

    async handleDmContactSelectedFromModal({ contactId, displayName }) {
        this.log.info(`Starting DM with contact: ${contactId} (${displayName})`);
        if (!this.currentProjectId) { // Use this.currentProjectId
            this.appClient.notifier.warn("Please select a project first.");
            return;
        }

        try {
            const channel = await this.appClient.rpcClient.call('MessagingRpc.getOrCreateDmChannel', {
                targetContactId: contactId
            }, { projectId: this.currentProjectId }); // Use this.currentProjectId

            if (channel && channel.channelId) {
                await this.conversationList.loadConversations();
                await this.handleConversationSelected({ conversationId: channel.channelId }); // Await
            } else {
                throw new Error("Failed to get or create DM channel.");
            }
        } catch (error) {
            this.log.error("Error starting DM:", error);
            this.appClient.notifier.error(`Could not start chat: ${error.message}`);
        }
    }

    async handleGroupCreationFromModal({ name, memberContactIds }) {
        this.log.info(`Creating group "${name}" with members:`, memberContactIds);
        if (!this.currentProjectId) { // Use this.currentProjectId
            this.appClient.notifier.warn("Please select a project first.");
            return;
        }
        try {
            const newChannel = await this.appClient.rpcClient.call('MessagingRpc.createChannel', {
                name,
                memberContactIds,
            }, { projectId: this.currentProjectId }); // Use this.currentProjectId

            if (newChannel && newChannel.channelId) {
                await this.conversationList.loadConversations();
                await this.handleConversationSelected({ conversationId: newChannel.channelId }); // Await
            } else {
                throw new Error("Failed to create group channel.");
            }
        } catch (error) {
            this.log.error("Error creating group:", error);
            this.appClient.notifier.error(`Could not create group: ${error.message}`);
        }
    }

    async handleMessageSentByChatView({ recipientType, recipientId, messageContent, attachments, messageType, metadata }) {
        if (!this.currentProjectId) {
            this.appClient.notifier.warn("Cannot send message: No project selected.");
            return;
        }
        const senderContactId = this.myContactId;
        const currentConversationId = this.activeConversationId;

        const tempMessageId = `temp_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`; // More unique tempId

        // Store that we are expecting a confirmation for this tempId
        this.optimisticMessages.set(tempMessageId, true); // Mark as pending confirmation

        this.chatView.handleIncomingMessage({
            _id: tempMessageId, // Use tempId for optimistic UI
            tempId: tempMessageId, // Explicitly store tempId
            conversationId: currentConversationId,
            senderContactId: senderContactId,
            senderDisplayName: this.appClient.getSharedData('user')?.username || 'Me',
            content: messageContent,
            attachments: attachments,
            messageType: messageType,
            timestamp: new Date().toISOString(),
            isOwnMessage: true,
            status: 'sending'
        });

        try {
            const payload = { messageContent, attachments, messageType, metadata, tempId: tempMessageId }; // Send tempId to server
            const context = { projectId: this.currentProjectId };
            let serverConfirmedMessage;

            if (recipientType === 'contact') {
                serverConfirmedMessage = await this.appClient.rpcClient.call('MessagingRpc.sendMessage',
                    { recipientType: 'contact', recipientId, ...payload }, context);
            } else if (recipientType === 'channel') {
                serverConfirmedMessage = await this.appClient.rpcClient.call('MessagingRpc.sendMessage',
                    { recipientType: 'channel', recipientId, ...payload }, context);
            }

            this.log.info("Message sent, server confirmed:", serverConfirmedMessage);

            // Update the optimistic message with the real _id and status
            if (serverConfirmedMessage && serverConfirmedMessage._id) {
                this.chatView.updateMessageStatus(tempMessageId, serverConfirmedMessage._id, 'sent');
                // Remove from optimistic tracking as it's now confirmed by direct RPC response
                this.optimisticMessages.delete(tempMessageId);
            }

        } catch (error) {
            this.log.error("Failed to send message via RPC:", error);
            this.appClient.notifier.error("Failed to send message.");
            this.chatView.updateMessageStatus(tempMessageId, null, 'failed');
            this.optimisticMessages.delete(tempMessageId); // Remove on error too
        }
    }


    // framework/client/components/MessagingClient.js
    handleNewMessageFromServer(eventData) {
        this.log.debug("MessagingClient: New message from server:", eventData);
        const { conversationId, projectId: messageProjectId, clientTempId, _id: serverId } = eventData;

        if (messageProjectId !== this.currentProjectId) {
            // ... (ignore if not for current project)
            return;
        }

        // Check if this message is a confirmation of an optimistically sent one
        if (clientTempId && this.optimisticMessages.has(clientTempId)) {
            this.log.info(`Server confirmed optimistic message (tempId: ${clientTempId}, serverId: ${serverId})`);
            // Update the existing optimistic message in ChatView with the server's _id and final status
            this.chatView.updateMessageStatus(clientTempId, serverId, 'sent'); // Or 'delivered'
            this.optimisticMessages.delete(clientTempId); // Remove from tracking

            // Update ConversationList (it might have an optimistic entry or need its last message updated)
            this.conversationList.handleIncomingMessage(eventData, true /* isConfirmation */);
            return; // Don't add it again via chatView.handleIncomingMessage
        }

        // If it's not a confirmation of an optimistic message, or if it's for another user, add it normally
        this.conversationList.handleIncomingMessage(eventData);

        if (this.activeConversationId === conversationId) {
            this.chatView.handleIncomingMessage(eventData);
        }
    }

    async handleConversationUpdatedFromServer(eventData) {
        this.log.debug("MessagingClient: Conversation updated from server:", eventData);

        if (eventData.projectId !== this.currentProjectId) {
            this.log.debug(`Ignoring conversation update for project ${eventData.projectId}`);
            return;
        }

        this.conversationList.handleConversationUpdate(eventData);
        if (this.activeConversationId === eventData.channelId) {
            this.chatView.handleConversationUpdate(eventData);
            // If group settings modal is open for this channel, refresh its data
            if (this.groupSettingsModal &&
                this.groupSettingsModal.channelId === eventData.channelId &&
                this.groupSettingsModal.modalInstance &&
                this.groupSettingsModal.modalInstance._isShown) { // Check if modal is currently shown
                await this.groupSettingsModal.refreshModalData();
            }
        }
    }

    adjustLayoutForScreenSize() {
        if (window.innerWidth <= 768) { // Mobile
            if (this.activeConversationId) {
                this.container.classList.add('viewing-chat');
            } else {
                this.container.classList.remove('viewing-chat');
            }
        } else { // Desktop
            this.container.classList.remove('viewing-chat');
        }
    }

    handleMessagesReadFromServer(eventData) { // NEW METHOD
        this.log.debug("MessagingClient: Messages read event from server:", eventData);
        const { conversationId, contactId: readerContactId, messageIds } = eventData;

        if (eventData.projectId !== this.currentProjectId) {
            this.log.debug(`Ignoring messagesRead event for project ${eventData.projectId}`);
            return;
        }

        // If the current user is the one who read the messages,
        // ConversationList might have already updated its unread counts optimistically.
        // This server event primarily serves to notify *other* members of the conversation
        // that messages have been read by someone.
        // For now, we don't have UI to show "Read by X, Y" in the message bubble itself,
        // but ChatView could be extended to handle this.

        if (this.activeConversationId === conversationId) {
            // If the active chat is the one where messages were read,
            // ChatView's MessageArea could potentially update read receipts on bubbles.
            this.chatView.handleMessagesReadByOther(readerContactId, messageIds);
        }

        // ConversationList might also want to know if *another* user read messages,
        // though its primary concern is the current user's unread count.
        // For now, no direct action in ConversationList for this specific event from others.
    }

    async render() {
        // If the container isn't even created yet (e.g., before initialize completes or if project not selected)
        if (!this.container) {
            this.container = document.createElement('div'); // Create a basic container
            this.container.className = 'messaging-client-container';
            this.container.id = `${this.config.instanceId}-main-wrapper`;
            // Render a placeholder if no project is selected yet or during initial load
            if (!this.currentProjectId) {
                this.renderPlaceholder("Please select a project to use messaging.");
            } else {
                // This case should ideally be handled by setupOnProjectChange which calls renderStructure
                // But as a fallback:
                const loadingDiv = document.createElement('div');
                loadingDiv.textContent = "Loading messaging client...";
                this.container.appendChild(loadingDiv);
            }
        } else if (!this.currentProjectId && this.placeholderElement && !this.container.contains(this.placeholderElement)) {
            // If project becomes null and placeholder isn't there, show it
            this.renderPlaceholder("Please select a project to use messaging.");
        }
        // The actual rendering of sub-components (convList, chatView) is handled by
        // renderStructure and updateChatViewPaneContent, which are called by
        // setupOnProjectChange or handleProjectSelected.
        return this.container;
    }

    async handleMessagesViewed({ conversationId, messageIds }) {
        if (!this.currentProjectId) return;
        if (messageIds && messageIds.length > 0) {
            try {
                await this.appClient.rpcClient.call('MessagingRpc.markMessagesAsRead', {
                    conversationId,
                    messageIds
                }, { projectId: this.currentProjectId }); // Use this.currentProjectId
                this.log.info(`Marked ${messageIds.length} messages as read in conversation ${conversationId}`);
            } catch (error) {
                this.log.error(`Failed to mark messages as read for conversation ${conversationId}:`, error);
            }
        }
    }

    async handleShowGroupSettings({ channelId }) {
        if (!this.currentProjectId) {
            this.appClient.notifier.warn("Please select a project first.");
            return;
        }
        if (this.groupSettingsModal) {
            // Pass the necessary properties to the modal before showing it
            this.groupSettingsModal.channelId = channelId;
            this.groupSettingsModal.myContactId = this.myContactId; // Ensure this.myContactId is correctly set
            this.groupSettingsModal.currentProjectId = this.currentProjectId;
            await this.groupSettingsModal.show();
        } else {
            this.log.error("GroupSettingsModal instance is not available.");
        }
    }

    async handleChannelDeletedFromServer({ channelId, projectId, deletedByName }) {
        if (projectId !== this.currentProjectId) {
            this.log.debug(`Ignoring channelDeleted event for project ${projectId}`);
            return;
        }
        this.log.info(`Channel ${channelId} was deleted by ${deletedByName}. Updating UI.`);
        if (this.activeConversationId === channelId) {
            this.activeConversationId = null;
            await this.updateChatViewPaneContent();
            this.adjustLayoutForScreenSize();
        }
        this.conversationList.removeConversation(channelId);
        // --- UNSUBSCRIBE FROM DELETED CHANNEL ROOM ---
        if (this.subscribedChannelRooms.has(channelId)) {
            try {
                await this.appClient.rpcClient.call('MessagingRpc.unsubscribeFromChannelUpdates', { channelId }, { projectId: this.currentProjectId });
                this.subscribedChannelRooms.delete(channelId);
            } catch (error) {
                this.log.error(`Failed to unsubscribe from deleted channel ${channelId} updates:`, error);
            }
        }
        // --- END UNSUBSCRIBE ---
    }


    async handleChannelMembershipChange(eventData) {
        if (eventData.projectId !== this.currentProjectId) {
            this.log.debug(`Ignoring channel membership change for project ${eventData.projectId}`);
            return;
        }
        this.log.info(`MessagingClient: Channel membership change event from server (type: ${eventData.specificEvent || 'conversationUpdated'}):`, eventData);

        if (this.conversationList) {
            // eventData should be the full updated channel object
            this.conversationList.handleConversationUpdate(eventData);
        }

        if (this.activeConversationId === eventData.channelId) {
            if (this.chatView) {
                // Pass the full eventData which is the updated channel object
                this.chatView.handleConversationUpdate(eventData);
            }
            if (this.groupSettingsModal &&
                this.groupSettingsModal.channelId === eventData.channelId &&
                this.groupSettingsModal.modalInstance &&
                this.groupSettingsModal.modalInstance._isShown) {
                await this.groupSettingsModal.refreshModalData();
            }
        }
    }

    async handleTypingStatusChangeFromInput({ isTyping }) {
        if (this.activeConversationId && this.currentProjectId) {
            try {
                await this.appClient.rpcClient.call('MessagingRpc.sendTypingIndicator', {
                    conversationId: this.activeConversationId,
                    isTyping: isTyping
                }, { projectId: this.currentProjectId });
            } catch (error) {
                this.log.error("Failed to send typing indicator:", error);
            }
        }
    }

    handleUserTypingStatusFromServer(eventData) {
        // { conversationId, projectId, contactId, displayName, isTyping }
        if (eventData.projectId !== this.currentProjectId) return;

        if (this.conversationList) {
            this.conversationList.updateTypingStatusForConversation(eventData);
        }

        if (this.activeConversationId === eventData.conversationId) {
            if (this.chatView) { // Pass to ChatView to update its header/indicator area
                this.chatView.updateTypingIndicator(eventData);
            }
        }
    }

    async handleShowContactInfo({ contactId }) {
        if (!this.currentProjectId) {
            this.appClient.notifier.warn("Please select a project first.");
            return;
        }
        if (this.groupSettingsModal && this.groupSettingsModal.modalInstance && this.groupSettingsModal.modalInstance._isShown) {
            this.groupSettingsModal.hide();
            // Optionally, wait for it to fully hide if there are issues with immediate reopening
            // await new Promise(resolve => setTimeout(resolve, 200)); // Adjust delay if needed
        }

        if (this.contactInfoModal) {
            await this.contactInfoModal.show(contactId, this.currentProjectId);
        } else {
            this.log.error("ContactInfoModal instance is not available.");
        }
    }

    async subscribeToLoadedConversationRooms() {
        if (!this.appClient.isLoggedIn() || !this.currentProjectId) return;
        const conversationsToSubscribe = this.conversationList.conversations || [];
        for (const conv of conversationsToSubscribe) {
            if (conv.channelId) {
                const key = `channel-updates-${conv.channelId}`;
                this.appClient.sessionTokenHandler.registerSubscription(
                    key,
                    'MessagingRpc.subscribeToChannelUpdates',
                    'MessagingRpc.unsubscribeFromChannelUpdates',
                    { channelId: conv.channelId, projectId: this.currentProjectId }
                );
                await this.appClient.sessionTokenHandler.subscribe(key);
            }
        }
    }

    async unsubscribeFromAllTrackedChannelRooms() {
        if (!this.appClient.isLoggedIn()) return;
        const sessionHandler = this.appClient.sessionTokenHandler;
        const keysToUnsubscribe = Array.from(sessionHandler.dynamicSubscriptions.keys())
            .filter(key => key.startsWith('channel-updates-'));
        this.log.info(`Unsubscribing from ${keysToUnsubscribe.length} channel rooms.`);
        // Use Promise.all to unsubscribe concurrently
        const unsubscribePromises = keysToUnsubscribe.map(key => sessionHandler.unsubscribe(key));
        await Promise.all(unsubscribePromises);
    }


    async destroy() {
        window.removeEventListener('resize', this._boundAdjustLayout);
        this.appClient.events.local.off('projectSelected', this._boundHandleProjectSelected);
        this.appClient.events.server.off('newMessage', this.handleNewMessageFromServer);
        this.appClient.events.server.off('conversationUpdated', this.handleConversationUpdatedFromServer);
        this.appClient.events.server.off('messagesRead', this.handleMessagesReadFromServer.bind(this));
        this.appClient.events.server.off('channelDeleted', this.handleChannelDeletedFromServer);
        this.appClient.events.server.off('channelMemberAdded', this.handleChannelMembershipChange);
        this.appClient.events.server.off('channelMemberRemoved', this.handleChannelMembershipChange);
        this.appClient.events.server.off('channelMemberRoleChanged', this.handleChannelMembershipChange);
        this.appClient.events.server.off('youWereRemovedFromChannel', this.handleYouWereRemoved);
        this.appClient.events.server.off('user_typing_status', this.handleUserTypingStatusFromServer);

        this.groupSettingsModal?.off('leftGroup', this.handleLeftGroup);
        this.groupSettingsModal?.off('contactInfoClicked', this.handleShowContactInfo);

        this.chatView?.off('messagesViewed', this.handleMessagesViewed);
        this.chatView?.messageInput?.off('typingStatusChanged', this.handleTypingStatusChangeFromInput);
        this.chatView?.header?.off('contactInfoClicked', this.handleShowContactInfo);

        this.contactInfoModal?.destroy();
        this.conversationList?.destroy();
        this.chatView?.destroy();
        this.newChatModal?.destroy();
        this.groupSettingsModal?.destroy();

        await this.unsubscribeFromAllTrackedChannelRooms();

        super.destroy();
    }
}